import React, { useContext, useState } from "react";
import useBreadcrumbs from "../utils/hooks/useBreadcrumbs";
import { FaUsers } from "react-icons/fa";
import Button from "../UI/Button/Button";
import Table from "../UI/Table/Table";
import Modal from "../UI/Modal/Modal";
import { PaginationType } from "../Types/APITypes";
import Alert from "../UI/Alert/Alert";
import { NotifyContext } from "../context/notifyContext";
import { Subscriber } from "../Types/newsletter";
import NewsletterAPI from "../services/api/newsletterService";
import AddOrUpdateSubscriber from "./Subscriber/AddOrUpdateSubscriber";
import DeleteSubscriber from "./Subscriber/DeleteSubscriber";
import UnsubscribeSubscriber from "./Subscriber/UnsubscribeSubscriber";

export default function SubscriberManagement() {
  useBreadcrumbs([
    {
      label: "Abonnés",
      icon: <FaUsers />,
      is_link: false,
    },
  ]);

  const notify = useContext(NotifyContext);

  const [error, setError] = useState("");
  const [modal_edit, setModalEdit] = useState<{
    open: boolean;
    subscriber?: Subscriber | null;
  }>({ open: false, subscriber: null });

  const [modal_delete, setModalDelete] = useState<{
    open: boolean;
    subscriber?: Subscriber | null;
  }>({ open: false, subscriber: null });

  const [modal_unsubscribe, setModalUnsubscribe] = useState<{
    open: boolean;
    subscriber?: Subscriber | null;
  }>({ open: false, subscriber: null });

  const columns = React.useMemo<any[]>(
    () => [
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Statut",
        accessor: "status",
      },
      {
        Header: "Date d'inscription",
        accessor: "created_at",
        Cell: ({ value }: any) => (
          <span>{new Date(value).toLocaleString()}</span>
        ),
      },
      {
        Header: "Action",
        Cell: ({ row }: any) => {
          return (
            <div className="flex items-center gap-2">
              <Button
                color="primary"
                onClick={() =>
                  setModalEdit({ open: true, subscriber: row.original })
                }
              >
                Editer
              </Button>
              <Button
                color="error"
                onClick={() =>
                  setModalDelete({ open: true, subscriber: row.original })
                }
              >
                Supprimer
              </Button>
              {row.original.status === "active" && (
                <Button
                  color="error"
                  onClick={() =>
                    setModalUnsubscribe({ open: true, subscriber: row.original })
                  }
                >
                  Désabonner
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const fetchData = React.useCallback(
    (pagination?: PaginationType) => {
      setLoading(true);
      NewsletterAPI.get_subscribers(pagination)
        // .then((response: any) => response?.data)
        .then((data: any) => {
          setData(data?.subscribers || []);
          setTableInfos({
            pageCount: data?.last_page || 1,
            total: data?.total || 0,
          });
          setError("");
        })
        .catch((error) => {
          setError(error);
          notify(error, "error");
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [notify]
  );

  const [data, setData] = React.useState<Subscriber[] | null | undefined>();
  const [loading, setLoading] = React.useState(true);
  const [table_infos, setTableInfos] = React.useState({
    pageCount: 0,
    total: 0,
  });

  return (
    <div className="">
      <Modal
        open={modal_edit.open}
        title={modal_edit.subscriber ? "Modifier l'abonné" : "Créer un abonné"}
        onClose={() => {
          setModalEdit({ open: false });
        }}
      >
        <AddOrUpdateSubscriber
          onClose={() => {
            fetchData();
            setModalEdit({ open: false });
          }}
          initialState={modal_edit.subscriber}
        />
      </Modal>

      <Modal
        open={modal_delete.open}
        title={`Supprimer ${modal_delete.subscriber?.email}`}
        onClose={() => {
          setModalDelete({ open: false });
        }}
      >
        <DeleteSubscriber
          onClose={() => {
            fetchData();
            setModalDelete({ open: false });
          }}
          subscriber={modal_delete.subscriber}
        />
      </Modal>

      <Modal
        open={modal_unsubscribe.open}
        title={`Désabonner ${modal_unsubscribe.subscriber?.email}`}
        onClose={() => {
          setModalUnsubscribe({ open: false });
        }}
      >
        <UnsubscribeSubscriber
          onClose={() => {
            fetchData();
            setModalUnsubscribe({ open: false });
          }}
          subscriber={modal_unsubscribe.subscriber}
        />
      </Modal>

      <div className="flex flex-col flex-1">
        {error && <Alert color="error">{error}</Alert>}

        {loading && table_infos.total === 0 && (
          <div className="flex items-center justify-center w-full p-8">
            <div role="status">
              <svg
                aria-hidden="true"
                className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        {table_infos.total === 0 && !loading && (
          <div className="flex flex-col items-center justify-center gap-4 p-4 py-8 border border-dashed rounded border-black/60">
            <p>Aucun abonné présent</p>
          </div>
        )}

        <Table
          columns={columns}
          data={data || []}
          fetchData={fetchData}
          loading={loading}
          pageCount={table_infos.pageCount}
          total={table_infos.total}
          className="h-[728px]"
        />
      </div>
    </div>
  );
} 